/**
 * **描く層の組み立て**（Issue #766。「台本 ＋ 時計 → 絵」の口の**絵の側**）。
 *
 * 受け取るのは**その時刻の台本**（`frame` ＝ `createDuelScript().frameAt(ms)`。演出の帯・盤面・HUD・舞台の設定の値）と素材
 * （カードの絵・プレイヤー・盤の位置・元の映像）だけで、台本の作り方（イベント列・並べ方・状況の分け方）を知らない
 * （`editorPorts/stageScript.boundary.test.js` が import で見張る）。中で決めるのは「どの盤（Field）を・どのカメラで・どの設定で描くか」と
 * 層（`duelStageLayers`）まで。描く層を three から別のエンジンへ替えるときは、ここから下を替える。
 *
 * `/duel-timing`・`/duel-editor` の共通（`useDuelStage` が呼ぶ）。
 *
 * @param frame その時刻の台本
 * @param theme 今のテーマ
 * @param settings 舞台の設定（完全な形。`shot` は名前か数値）
 * @param stage テーマの既定に重ねる舞台の設定（画質・テーマの見た目の値など）
 * @param extra 層に渡す追加の設定。**フレームの関数**でもよい
 * @param players プレイヤーの情報
 * @param cards カードの引き方（`useCardAccess`）
 * @param board 状態チャンネルが無いときの盤面 1 枚（`OcgGetState` の `board`）
 * @param zoneConfig 盤の位置の出どころ（`{ overlay_zones }` を持つもの＝ field_gen の config か、外から渡したゾーン）
 * @param video 元の映像 `{ el, size, crop? }`（`el` は drawImage できる要素。`size` は元の映像の寸法、`crop` は `el` が元の映像のどの矩形か。Issue #808）
 */
import { useEffect, useMemo, useState } from 'react';
import { CANVAS } from '../duelEditor/canvas/Canvas';
import { EMPTY_FIELD } from '../duelEditor/core/field';
import { liveField, planeForVideo, resolveLiveZones } from '../duelEditor/fields/live';
import { virtualField } from '../duelEditor/fields/virtual';
import { SHOTS, DEFAULT_SHOT_NAME } from '../duelEditor/stage/camera';
import { stageView } from '../duelEditor/stage/view';
import { preloadImages } from '../duelEditor/canvas/images';
import { gradeFilter, sceneShot } from '../editorPorts/fxScenePicture';
import { duelStageLayers } from '../editorPorts/duelRenderer';

/** ショット（名前か数値）→ 数値のショット */
export const shotOf = (shot) => (typeof shot === 'string' ? (SHOTS[shot] ?? SHOTS[DEFAULT_SHOT_NAME]) : (shot ?? SHOTS[DEFAULT_SHOT_NAME]));

export function useDuelPicture({
  frame, theme, settings, stage = null, extra = null,
  players = [], cards = null, board = null, zoneConfig = null, video = null,
}) {
  const videoEl = video?.el ?? null;
  const videoSize = video?.size ?? null;
  const videoCrop = video?.crop ?? null;

  // 書体と盤の紙の画像を先に読む（読めていない間に焼くと代わりのまま残る。Issue #549 / #565）
  const [fontReady, setFontReady] = useState(false);
  useEffect(() => {
    let alive = true;
    const fonts = typeof document !== 'undefined' ? document.fonts : null;
    if (!fonts?.load) { setFontReady(true); return undefined; }
    fonts.load('900 100px "GenShinGothic Heavy FX"').then(() => { if (alive) setFontReady(true); }).catch(() => { if (alive) setFontReady(true); });
    return () => { alive = false; };
  }, []);
  const [imagesReady, setImagesReady] = useState('');
  useEffect(() => {
    let alive = true;
    preloadImages(theme.images).then(() => { if (alive) setImagesReady(theme.name); });
    return () => { alive = false; };
  }, [theme]);

  // 盤（Field）: 実写は config のゾーンか同梱マットの推定、仮想は理想のグリッド
  const choice = settings.field;
  const live = useMemo(() => resolveLiveZones(zoneConfig, videoSize), [zoneConfig, videoSize]);
  const plane = useMemo(() => planeForVideo(videoSize), [videoSize]);
  const field = useMemo(() => {
    if (choice === 'empty') return EMPTY_FIELD;
    if (choice === 'virtual') return virtualField({ plane });
    if (!live.zones || !videoSize) return EMPTY_FIELD;
    return liveField({ zones: live.zones, videoSize, plane });
  }, [choice, live, videoSize, plane]);

  // 演出が求める舞台（仮想フィールド・ショット・色。Issue #535）。値は台本（`fx_scene` の状態チャンネル）から
  const fxScene = frame?.states?.fx_scene ?? null;
  const wantVirtual = fxScene?.field === 'virtual';
  const shownField = useMemo(
    () => (wantVirtual && choice !== 'virtual' ? virtualField({ plane }) : field),
    [wantVirtual, choice, plane, field],
  );
  const baseShot = useMemo(() => shotOf(settings.shot), [settings.shot]);
  const shownShot = useMemo(
    () => sceneShot(fxScene, baseShot),
    [fxScene?.shot, fxScene?.camera, fxScene?.shakeOffset, baseShot], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const view = useMemo(() => stageView(shownField, shownShot, CANVAS), [shownField, shownShot]);

  // 舞台の設定（テーマの既定 ＋ 画質など ＋ この画面の設定）
  const { fieldLayers, objectLayers, zoneLabels, hud } = settings;
  const stageConfig = useMemo(
    () => ({ ...theme.options, ...(stage ?? {}), layers: fieldLayers, objects: objectLayers, zoneLabels, hud: { show: hud } }),
    [theme, stage, fieldLayers, objectLayers, zoneLabels, hud],
  );
  // 元の映像の包みは**映像が変わったときだけ**作り直す（盤の紙の deps。カメラが動くたびに作り直すと紙を描き直す）
  const videoRef = useMemo(() => ({ el: videoEl, size: videoSize, crop: videoCrop }), [videoEl, videoSize, videoCrop]);
  const renderConfig = useMemo(() => ({
    theme, view, canvas: CANVAS, stage: stageConfig, players,
    field,
    video: videoRef,
    cards,
    // イベントがまだ無い replay では、状態チャンネルの代わりにこの 1 枚に落ちる
    board,
    fontReady,
    imagesReady,
  }), [theme, view, stageConfig, players, field, videoRef, cards, board, fontReady, imagesReady]);

  const extraNow = typeof extra === 'function' ? extra(frame) : extra;
  const layers = duelStageLayers(frame, { config: { ...renderConfig, ...(extraNow ?? {}) } });

  return {
    fontReady, imagesReady, live, field, view, fxScene, filter: gradeFilter(fxScene?.grade), renderConfig, layers,
  };
}
